import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSettings } from "../../context/SettingsContext";
import { AdminThemeProvider, useAdminTheme } from "../../context/AdminThemeContext";
import {
  LayoutDashboard, Disc, Users, FileText, Link2, Download, Settings, LogOut, Menu, X,
  Mic2, ImageIcon, ExternalLink, Megaphone, Sun, Moon, CheckSquare, ChevronDown, Inbox, HardDrive, Disc3,
  Music, Share2,
} from "lucide-react";
import { AdminNotificationBell } from "./AdminNotificationBell";

// ── Navigation-Struktur ───────────────────────────────────────────────────────
const NAV_STRUCTURE = [
  { type: "item", to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },

  { type: "divider" },

  {
    type: "group", label: "Releases", icon: Disc,
    children: [
      { to: "/admin/releases", label: "Alle Releases" },
      { to: "/admin/release-proposals", label: "Vorschläge", badge: "proposals" },
    ]
  },
  {
    type: "group", label: "Artists", icon: Mic2,
    children: [
      { to: "/admin/artists", label: "Alle Artists" },
      { to: "/admin/bio-approvals", label: "Bio-Genehmigungen" },
      { to: "/admin/artist-applications", label: "Bewerbungen", developerOnly: true },
      { to: "/admin/spotlight", label: "Spotlight-Plan", developerOnly: true },
    ]
  },
  {
    type: "group", label: "Labels", icon: Disc3,
    children: [
      { to: "/admin/labels", label: "Alle Labels" },
      { to: "/admin/label-applications", label: "Label-Bewerbungen" },
    ]
  },

  { type: "divider" },

  { type: "item", to: "/admin/media",   label: "Mediathek", icon: ImageIcon, developerOnly: true },
  { type: "item", to: "/admin/pages",   label: "Seiten",    icon: FileText,  developerOnly: true },
  { type: "item", to: "/admin/users",   label: "Benutzer",  icon: Users,     developerOnly: true },

  { type: "divider", developerOnly: true },

  {
    type: "group", label: "Developer", icon: Settings, developerOnly: true,
    children: [
      { to: "/admin/spotify-playlist",  label: "Spotify Playlist" },
      { to: "/admin/social-media",      label: "Social Media" },
      { to: "/admin/settings",          label: "Site-Einstellungen" },
      { to: "/admin/affiliate",         label: "Affiliate-Regeln" },
      { to: "/admin/stripe-sync",       label: "Stripe Sync" },
      { to: "/admin/partner",           label: "Werbeplätze" },
      { to: "/admin/import-export",     label: "Import / Export" },
      { to: "/admin/backup",            label: "Backup" },
      { to: "/admin/collabs",           label: "Collab Finder" },
    ]
  },
];

// ── Hilfsfunktion: ist ein Gruppen-Pfad aktiv? ────────────────────────────────
function groupIsActive(children, pathname) {
  return children.some(c => pathname === c.to || pathname.startsWith(c.to + "/"));
}

// ── Einzelner Gruppen-Eintrag (Akkordeon) ─────────────────────────────────────
function NavGroup({ item, location, onNavigate, isDark }) {
  const active = groupIsActive(item.children, location.pathname);
  const [open, setOpen] = useState(active);

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
          active
            ? "text-rose-400 font-semibold"
            : "text-zinc-400 hover:text-white hover:bg-zinc-800"
        }`}
      >
        <item.icon size={16} className="flex-shrink-0" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-7 mt-0.5 space-y-0.5 border-l border-white/[0.07] pl-3">
          {item.children.map(child => {
            const childActive = location.pathname === child.to || location.pathname.startsWith(child.to + "/");
            return (
              <Link key={child.to} to={child.to} onClick={onNavigate}
                className={`flex items-center justify-between py-2 px-2 rounded-md text-xs transition-all ${
                  childActive ? "text-rose-400 font-semibold" : "text-zinc-500 hover:text-white"
                }`}>
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout({ children }) {
  return (
    <AdminThemeProvider>
      <AdminLayoutInner>{children}</AdminLayoutInner>
    </AdminThemeProvider>
  );
}

function AdminLayoutInner({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const { isDark, toggleTheme } = useAdminTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const isDevOrAdmin = user?.role === "developer" || user?.role === "admin";

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-white/5">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt={settings.site_name || "HUWND"} className="h-8 w-auto object-contain max-w-[100px]" />
            ) : (
              <>
                <div className="w-8 h-8 bg-rose-600 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-black text-sm font-heading">H</span>
                </div>
                <div>
                  <p className="text-white font-black text-sm font-heading leading-none">HUWND</p>
                  <p className="text-zinc-500 text-xs">Admin</p>
                </div>
              </>
            )}
          </Link>
          <AdminNotificationBell token={user?.token} />
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV_STRUCTURE.map((item, idx) => {
          // Dev-only ausblenden
          if (item.developerOnly && user?.role !== "developer") return null;

          if (item.type === "divider") {
            return <div key={idx} className="h-px bg-white/[0.05] my-1.5 mx-1" />;
          }

          if (item.type === "group") {
            const visibleChildren = item.children.filter(c => !c.developerOnly || user?.role === "developer");
            if (visibleChildren.length === 0) return null;
            return (
              <NavGroup
                key={item.label}
                item={{ ...item, children: visibleChildren }}
                location={location}
                onNavigate={() => setSidebarOpen(false)}
                isDark={isDark}
              />
            );
          }

          // type === "item"
          const Icon = item.icon;
          const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          return (
            <Link key={item.to} to={item.to} onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all ${
                active ? "bg-rose-600/15 text-rose-400 font-semibold" : "text-zinc-400 hover:text-white hover:bg-zinc-800"
              }`}>
              <Icon size={16} className="flex-shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-white/5">
        <button onClick={toggleTheme} data-testid="admin-theme-toggle"
          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-colors mb-1 ${
            isDark ? "text-zinc-400 hover:text-white hover:bg-zinc-800" : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200"
          }`}>
          <span className="flex items-center gap-3">
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
            {isDark ? "Light" : "Dark"}
          </span>
          <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${isDark ? "bg-rose-600" : "bg-zinc-400"}`}>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${isDark ? "translate-x-4" : "translate-x-0"}`} />
          </div>
        </button>
        <Link to="/"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md text-sm transition-colors mb-1">
          <ExternalLink size={16} /> Zur Website
        </Link>
        <div className="px-3 py-2 mb-1">
          <p className="text-zinc-300 text-xs font-semibold truncate">{user?.username || user?.email}</p>
          <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${
            user?.role === "developer" ? "bg-purple-900/40 text-purple-400" : "bg-zinc-800 text-zinc-400"
          }`}>{user?.role || "admin"}</span>
        </div>
        <button onClick={handleLogout} data-testid="admin-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md text-sm transition-colors">
          <LogOut size={16} /> Abmelden
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex overflow-x-hidden" style={{ background: isDark ? "#09090b" : "#f8fafc" }}>
      <aside className="hidden lg:flex lg:flex-col w-56 bg-zinc-950 border-r border-white/5 fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
        <aside className={`absolute left-0 top-0 bottom-0 w-64 bg-zinc-950 border-r border-white/5 flex flex-col z-50 transition-transform duration-300 ease-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <SidebarContent />
        </aside>
      </div>
      <div className={`flex-1 lg:ml-56 flex flex-col min-h-screen overflow-x-hidden${isDark ? "" : " admin-light"}`}>
        <div className={`lg:hidden flex items-center justify-between h-14 px-4 sticky top-0 z-20 shadow-sm ${isDark ? "bg-zinc-900 border-b border-white/5" : "bg-white border-b border-zinc-200"}`}>
          <button onClick={() => setSidebarOpen(true)} className={isDark ? "text-zinc-400" : "text-zinc-600"}>
            <Menu size={20} />
          </button>
          <span className={`font-bold font-heading text-sm ${isDark ? "text-white" : "text-zinc-900"}`}>HUWND Admin</span>
          <div className="flex items-center gap-1">
            <AdminNotificationBell token={user?.token} />
            <button onClick={toggleTheme} className={isDark ? "text-zinc-400" : "text-zinc-600"}>
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
